/**
 * Time zones in the portal. Every time Stitch shows is in one zone, the "display zone": the
 * person's own choice, else their company's, else their device's. The shell sets it before its
 * pages render, and the formatters in format.ts use it.
 */

let zone: string | undefined;

export function deviceZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function setDisplayZone(tz: string | undefined): void {
  zone = tz && isZone(tz) ? tz : undefined;
}

export function displayZone(): string {
  return zone ?? deviceZone();
}

export function isZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * The zone someone sees times in (1.12.13). Their choice: null (or 'device', from before) is this
 * device's own zone (Automatic), for everyone; 'company' their company's (the device's where there
 * is none); else a zone they chose. Hours, holidays, reports' days and the wallboard's "today"
 * stay on the company's clock wherever the person is, and say so.
 */
export function effectiveZone(choice: string | null | undefined, company: string | null | undefined): string {
  if (!choice || choice === 'device') return deviceZone();
  if (choice === 'company') return company && isZone(company) ? company : deviceZone();
  return isZone(choice) ? choice : deviceZone();
}

function part(tz: string, style: Intl.DateTimeFormatOptions['timeZoneName']): string {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: style }).formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/** "Central Time" (all year), falling back to "Central Daylight Time". */
export function zoneName(tz: string): string {
  if (tz === 'UTC' || tz === 'Etc/UTC') return 'Coordinated Universal Time';
  const generic = part(tz, 'longGeneric');
  return generic && !/^GMT[+-]/.test(generic) ? generic : part(tz, 'long') || tz;
}

/** "Times in Central Time": for pages full of times, so nobody wonders whose clock it is. */
export function timesIn(): string {
  return `Times in ${zoneName(displayZone())}`;
}

/** Today's date in a time zone, as a local Date at noon (for date arithmetic that ignores the clock). */
export function todayIn(tz: string, now = new Date()): Date {
  try {
    const [y, m, d] = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now).split('-').map(Number);
    if (y && m && d) return new Date(y, m - 1, d, 12);
  } catch {
    // Falls through to the device's date.
  }
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
}

/** "Chicago" from America/Chicago. */
export function zoneCity(tz: string): string {
  return (tz.split('/').pop() ?? tz).replace(/_/g, ' ');
}

/** "Central Time (Chicago)". */
export function zoneLabel(tz: string): string {
  const name = zoneName(tz);
  const city = zoneCity(tz);
  return tz.includes('/') && !name.includes(city) ? `${name} (${city})` : name;
}

/** "UTC−5" or "UTC+5:30", right now. */
export function zoneOffset(tz: string): string {
  const o = part(tz, 'shortOffset').replace('GMT', 'UTC').replace('-', '−');
  return o === 'UTC' ? 'UTC±0' : o;
}

/** Minutes ahead of UTC, right now, for sorting. */
function offsetMinutes(tz: string): number {
  const m = /UTC([+−])(\d{1,2})(?::(\d{2}))?/.exec(zoneOffset(tz));
  if (!m) return 0;
  const n = Number(m[2]) * 60 + Number(m[3] ?? 0);
  return m[1] === '−' ? -n : n;
}

/** The zones most Stitch customers are in, first in the list. */
export const COMMON_ZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Puerto_Rico',
  'America/Halifax',
  'America/St_Johns'
];

let all: { value: string; label: string }[] | null = null;

/** Every zone the browser knows, by offset then name: "UTC−5 · Eastern Time (New York)". */
export function allZones(): { value: string; label: string }[] {
  if (all) return all;
  let names: string[] = [];
  try {
    names = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
  } catch {
    names = [];
  }
  if (!names.length) names = [...COMMON_ZONES, 'UTC', 'Europe/London', 'Europe/Paris', 'Asia/Kolkata', 'Asia/Tokyo', 'Australia/Sydney'];
  all = names
    .filter((z) => z.includes('/') || z === 'UTC')
    .map((z) => ({ z, off: offsetMinutes(z) }))
    .sort((a, b) => a.off - b.off || a.z.localeCompare(b.z))
    .map(({ z }) => ({ value: z, label: `${zoneOffset(z)} · ${zoneLabel(z)}` }));
  return all;
}

// --- coworkers' local time (1.12.13) ---------------------------------------------------------------

/** "9:14 AM" in a zone, now. */
export function timeIn(tz: string, now: Date = new Date()): string {
  try {
    return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz }).format(now);
  } catch {
    return '';
  }
}

/** "Thu" when it's another day there than here (in `here`'s zone), else null. */
export function dayThereIfDifferent(tz: string, here: string, now: Date = new Date()): string | null {
  try {
    const day = (zone: string) => new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: zone }).format(now);
    const there = day(tz);
    return there === day(here) ? null : there;
  } catch {
    return null;
  }
}

/** Minutes a zone is ahead of another right now (negative: behind). */
export function minutesApart(tz: string, from: string): number {
  return offsetMinutes(tz) - offsetMinutes(from);
}

/** "3h", "9h 30m", "45m": how far apart, without the direction. */
export function apartShort(minutes: number): string {
  const m = Math.abs(minutes);
  const h = Math.floor(m / 60);
  const r = m % 60;
  return h && r ? `${h}h ${r}m` : h ? `${h}h` : `${r}m`;
}

/** "3 hours behind you", "1 hour ahead of you", "9 hours 30 minutes ahead of you", "Same time as you". */
export function apartText(minutes: number): string {
  if (!minutes) return 'Same time as you';
  const m = Math.abs(minutes);
  const h = Math.floor(m / 60);
  const r = m % 60;
  const parts = [h ? `${h} hour${h === 1 ? '' : 's'}` : '', r ? `${r} minutes` : ''].filter(Boolean).join(' ');
  return `${parts} ${minutes > 0 ? 'ahead of' : 'behind'} you`;
}

/** Their day: 8 AM to 6 PM where they are. Outside it, Stitch says it's late for them. */
export const DAY_STARTS = 8;
export const DAY_ENDS = 18;

/** Outside 8 AM to 6 PM in a zone, now. */
export function afterHours(tz: string, now: Date = new Date()): boolean {
  try {
    const h = Number(new Intl.DateTimeFormat('en-GB', { hour: '2-digit', hourCycle: 'h23', timeZone: tz }).format(now));
    return h < DAY_STARTS || h >= DAY_ENDS;
  } catch {
    return false;
  }
}

/** North America's zones by their names ("Pacific"); elsewhere the city ("London", "Manila"). */
const US_NAMES = /^(Eastern|Central|Mountain|Pacific|Alaska|Hawaii-Aleutian|Atlantic|Newfoundland)( Time)?$/;
export function shortZoneName(tz: string): string {
  const generic = part(tz, 'longGeneric').replace(/ Standard Time$/, ' Time');
  const m = US_NAMES.exec(generic);
  if (m) return m[1] === 'Hawaii-Aleutian' ? 'Hawaii' : m[1]!;
  if (tz === 'UTC' || tz === 'Etc/UTC') return 'UTC';
  return zoneCity(tz);
}
